/**
 * /dashboard/approvals — unified approvals inbox (SOP §1.2;
 * 2026-06-01 pivot).
 *
 * Combines the two old "Approvals" surfaces that competed on the same
 * word — the internal `[My week][Approvals]` Tabs inside Timesheet and
 * the strip's old "Approvals" link to /dashboard/leave/approvals —
 * into ONE canonical discovery point. The dedicated
 * /dashboard/leave/approvals and /dashboard/timesheet/inbox routes are
 * preserved as deep links (they'll land alongside the Leave/Timesheet
 * hubs in Phase 3b).
 *
 * Backend RBAC is the boundary. This page is approver-gated for UX
 * (non-approvers see a friendly empty-state instead of a 403'd table),
 * but the underlying inbox endpoints are server-scoped anyway.
 *
 * URL state in ?tab=leave|timesheet; default = leave (higher
 * frequency). Tab badges show pending counts from the same queries the
 * panels themselves use, so there's no extra round-trip and no risk of
 * the badge disagreeing with the panel content.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/approvals/page.tsx. Swaps:
 *   - "use client" / <Suspense> → not needed (no SSR boundary)
 *   - useSearchParams / useRouter → Route.useSearch() +
 *     useNavigate() with the route's typed search schema
 *   - per-module layout.tsx → shared TimeAndLeaveShell
 */

import { useCallback } from "react";
import {
  createFileRoute,
  useNavigate,
 useParams,} from "@tanstack/react-router";
import { z } from "zod";
import { CalendarDays, Clock, Inbox as InboxIcon } from "lucide-react";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { LeaveApprovalsTab } from "@/components/leave/leave-approvals-tab";
import { ApprovalsTab as TimesheetApprovalsTab } from "@/components/timesheet/approvals-tab";
import { useLeaveRequests } from "@/hooks/use-leave";
import { useInbox } from "@/hooks/use-timesheet";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

type ApprovalsTabKey = "leave" | "timesheet";

// Search-param schema. TanStack Router validates every nav into
// /dashboard/approvals against this — invalid shapes are stripped, not
// coerced into runtime bugs. Mirrors the per-route Zod pattern Phase 2
// established for the _auth routes (see _auth/login.tsx).
const approvalsSearchSchema = z.object({
  tab: z.enum(["leave", "timesheet"]).optional(),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/approvals")({
  validateSearch: approvalsSearchSchema,
  component: ApprovalsRoute,
});

function ApprovalsRoute() {
  return (
    <TimeAndLeaveShell
      title="Approvals"
      description="Time-off and timesheet items waiting on your sign-off."
    >
      <ApprovalsBody />
    </TimeAndLeaveShell>
  );
}

function NonApproverEmptyState() {
  return (
    <Alert>
      <InboxIcon className="h-4 w-4" />
      <AlertTitle>No approval queues</AlertTitle>
      <AlertDescription>
        You don&apos;t currently have any approval queues. Managers and
        HR admins see leave requests and timesheet weeks awaiting their
        sign-off here.
      </AlertDescription>
    </Alert>
  );
}

function ApprovalsBody() {
  const { isApprover } = useLeaveRoles();
  if (!isApprover) return <NonApproverEmptyState />;
  return <ApprovalsInner />;
}

const ALL_TABS: readonly ApprovalsTabKey[] = [
  "leave",
  "timesheet",
] as const;

function isApprovalsTabKey(v: string): v is ApprovalsTabKey {
  return (ALL_TABS as readonly string[]).includes(v);
}

function ApprovalsInner() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const search = Route.useSearch();
  const navigate = useNavigate();
  const activeTab: ApprovalsTabKey = search.tab ?? "leave";

  const setTab = useCallback(
    (value: string) => {
      const next: ApprovalsTabKey = isApprovalsTabKey(value)
        ? value
        : "leave";
      void navigate({
        to: "/app/$tenantSlug/approvals", params: { tenantSlug },
        search: { tab: next },
        replace: true,
      });
    },
    [navigate],
  );

  // Pending-count badges. We piggy-back the same queries the panels use
  // (react-query dedupes by key) so opening the page makes ONE call per
  // tab regardless of how many things read the count.
  const leavePending = useLeaveRequests("pending");
  const timesheetPending = useInbox("submitted");

  const leaveCount = leavePending.data?.leave_requests.length ?? 0;
  const timesheetCount = timesheetPending.data?.items.length ?? 0;

  return (
    <Tabs
      value={activeTab}
      onValueChange={setTab}
      className="space-y-4"
    >
      <TabsList>
        <TabsTrigger value="leave" className="gap-1.5">
          <CalendarDays className="h-4 w-4" />
          Leave
          {leaveCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary tabular-nums">
              {leaveCount}
            </span>
          )}
        </TabsTrigger>
        <TabsTrigger value="timesheet" className="gap-1.5">
          <Clock className="h-4 w-4" />
          Timesheet
          {timesheetCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary tabular-nums">
              {timesheetCount}
            </span>
          )}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="leave">
        <LeaveApprovalsTab />
      </TabsContent>
      <TabsContent value="timesheet">
        <TimesheetApprovalsTab />
      </TabsContent>
    </Tabs>
  );
}
